import React, { useState, useEffect } from "react";
import { Table, Button, Input, message, Modal } from "antd";
import { Edit, Trash2 } from "lucide-react";
import DepartmentModal from "../../../../../modal/admin/addDepartmentModal";
import EditDepartmentModal from "../../../../../modal/admin/editDepartmentModal";
import { PlusOutlined, BulbOutlined } from "@ant-design/icons";
import { getRequest, deleteRequest } from "../../../../../service/index";
import { getEmployeeData } from "../../../../../utils/localStorage";

const Department = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [searchText, setSearchText] = useState("");

  const employeeData = getEmployeeData();
  const userRole = employeeData?.role;
  const departmentPermissions = employeeData?.designation?.permissions?.Organization?.Department || [];

  const canView = userRole === "companyadmin" || departmentPermissions.includes("view");
  const canAdd = userRole === "companyadmin" || departmentPermissions.includes("add");
  const canEdit = userRole === "companyadmin" || departmentPermissions.includes("edit");
  const canDelete = userRole === "companyadmin" || departmentPermissions.includes("delete");

  const fetchDepartments = async () => {
    try {
      setLoading(true);
      if (!canView) {
        setDepartments([]);
      } else {
        const response = await getRequest('/api/department');
        if (response && response.departments) {
          setDepartments(response.departments);
        }
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
      message.error('Failed to fetch departments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDepartments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView]);

  const handleDelete = async (record) => {
    if (!canDelete) {
      message.warning("You don't have permission to delete departments.");
      return;
    }
    Modal.confirm({
      title: 'Are you sure you want to delete this department?',
      content: `This will permanently delete the department "${record.name}" and all its sub-departments.`,
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'No, Cancel',
      onOk: async () => {
        try {
          const response = await deleteRequest(`/api/department?departmentId=${record._id}`);
          if (response.message === "Department deleted successfully") {
            message.success('Department deleted successfully');
            fetchDepartments();
          } else {
            message.error(response.message || 'Failed to delete department');
          }
        } catch (error) {
          console.error('Error deleting department:', error);
          message.error('Failed to delete department');
        }
      },
    });
  };

  const handleSearch = (value) => {
    setSearchText(value);
  };

  const filteredDepartments = departments.filter(dept => 
    dept.name.toLowerCase().includes(searchText.toLowerCase()) ||
    (dept.headOfDepartmentDetails?.name || "").toLowerCase().includes(searchText.toLowerCase()) ||
    (dept.subDepartments || []).some(sub => 
      sub.name.toLowerCase().includes(searchText.toLowerCase())
    )
  );

  const columns = [
    {
      title: "Department Name",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: "Head of Department",
      dataIndex: "headOfDepartmentDetails",
      key: "headOfDepartmentDetails",
      render: (headOfDepartmentDetails) => {
        if (!headOfDepartmentDetails?.name) return "-";
        return headOfDepartmentDetails.name;
      },
    },
    {
      title: "Employee Count",
      dataIndex: "employeeCount",
      key: "employeeCount",
      render: (count) => count || 0,
      sorter: (a, b) => (a.employeeCount || 0) - (b.employeeCount || 0),
    },
    {
      title: "Sub Departments",
      dataIndex: "subDepartments",
      key: "subDepartments",
      render: (subDepartments) => subDepartments?.map(sub => sub.name).join(", ") || "-",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <div className="flex gap-2">
          <Button
            type="text"
            icon={<Edit className="w-4 h-4 text-blue-600" />}
            disabled={!canEdit}
            onClick={() => {
              if (!canEdit) {
                message.warning("You don't have permission to edit departments.");
                return;
              }
              setSelectedDepartment(record);
              setIsEditModalOpen(true);
            }}
          />
          <Button
            type="text"
            icon={<Trash2 className="w-4 h-4 text-red-600" />}
            disabled={!canDelete}
            onClick={() => handleDelete(record)}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="px-4 pt-2 pb-4 flex flex-col gap-5 bg-white rounded-md">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold mb-4">Department</h2>
        <div className="flex justify-between items-center gap-2">
          <Button icon={<BulbOutlined />}>Page Tip</Button>
        </div>
      </div>
      <div className="flex justify-between items-center mb-6">
        <Input.Search
          placeholder="Search departments..."
          style={{ width: 300 }}
          className="mr-4"
          onChange={(e) => handleSearch(e.target.value)}
          allowClear
        />

        <Button
          type="primary"
          icon={<PlusOutlined />}
          className="bg-primary text-white"
          disabled={!canAdd}
          onClick={() => canAdd ? setIsModalOpen(true) : message.warning("You don't have permission to add departments.")}
        >
          Add Department
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={canView ? filteredDepartments : []}
        rowKey="_id"
        loading={loading}
        scroll={{ x: 'max-content' }}
        pagination={{
          total: canView ? filteredDepartments.length : 0,
          pageSize: 10,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} items`,
        }}
        className="bg-white rounded-lg border border-gray-200"
      />

      <DepartmentModal
        open={isModalOpen}
        onRequestClose={() => {
          setIsModalOpen(false);
          fetchDepartments();
        }}
      />

      <EditDepartmentModal
        open={isEditModalOpen}
        onRequestClose={() => {
          setIsEditModalOpen(false);
          setSelectedDepartment(null);
          fetchDepartments();
        }}
        department={selectedDepartment}
      />
      {!canView && (
        <div className="text-center text-gray-500 py-4">You don't have permission to view departments.</div>
      )}
    </div>
  );
};

export default Department;
